import { useState } from 'react';
import { Box, Svg, Text } from '@hopskipdrive/react-components';
import { Divider, Menu, MenuItem } from '@mui/material';
import { useFlags } from '@rideiq/contexts/LDContext';
import { QueryParamsForAssignRiders } from '@rideiq/organizers/types';
import { BulkDeactivateModal } from '@rideiq/riders/features/RidersTable/RidersSelectionBanner/BulkDeactivateModal';
import { BulkReactivateModal } from '@rideiq/riders/features/RidersTable/RidersSelectionBanner/BulkReactivateModal';
import { MoveToInvoiceGroupModal } from '@rideiq/riders/features/RidersTable/RidersSelectionBanner/MoveToInvoiceGroupModal';
import { Button } from '@rideiq/shared/components/Button';
import { colorPalette, colorTokens, variables } from '@rideiq/shared/components/variables';
import pluralize from 'pluralize';
import styled from 'styled-components';

interface RidersSelectionBannerProps {
  selectedRiderIds: number[];
  pageRiderCount: number;
  totalCount: number;
  isAllFilteredSelected: boolean;
  onSelectAllFiltered: () => void;
  onClearSelection: () => void;
  onMoveSuccess: () => void;
  queryParams: QueryParamsForAssignRiders;
}

const getSelectionState = (
  selectedRiderIds: number[],
  pageRiderCount: number,
  totalCount: number,
  isAllFilteredSelected: boolean
) => {
  const selectedCount = selectedRiderIds.length;
  const allPageSelected = selectedCount === pageRiderCount;
  const showSelectAllFiltered = allPageSelected && !isAllFilteredSelected && totalCount > pageRiderCount;
  const allSelectionText = isAllFilteredSelected ? `All ${totalCount} riders in this filtered view are selected.` : '';
  const allPartialSelectionText = allPageSelected
    ? `All ${pageRiderCount} riders on this page are selected.`
    : `${pluralize('rider', selectedCount, true)} ${pluralize('is', selectedCount)} selected.`;
  const selectionText = allSelectionText || allPartialSelectionText;

  return { showSelectAllFiltered, selectionText };
};

interface SelectionActionsProps {
  showSelectAllFiltered: boolean;
  totalCount: number;
  onSelectAllFiltered: () => void;
  onMoveToInvoiceGroup: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  isDeactivatedTab: boolean;
  showActionsMenu: boolean;
  onClearSelection: () => void;
}

const SelectionActions = ({
  showSelectAllFiltered,
  totalCount,
  onSelectAllFiltered,
  onMoveToInvoiceGroup,
  onDeactivate,
  onReactivate,
  isDeactivatedTab,
  showActionsMenu,
  onClearSelection,
}: SelectionActionsProps) => {
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement | null>(null);

  const closeActionsMenu = () => setActionsMenuAnchor(null);

  return (
    <>
      {showSelectAllFiltered && (
        <Button color="tertiary" size="small" onClick={onSelectAllFiltered}>
          {`Select all ${totalCount} riders in this filtered view`}
        </Button>
      )}
      <Box flex={1} />
      {showActionsMenu ? (
        <>
          <Button
            color="secondary"
            size="medium"
            endIcon={<Svg width="15px" height="15px" icon="arrow" style={{ padding: '2px 4px' }} />}
            onClick={(event) => setActionsMenuAnchor(event.currentTarget)}
          >
            Actions
          </Button>
          <Menu
            anchorEl={actionsMenuAnchor}
            open={!!actionsMenuAnchor}
            onClose={closeActionsMenu}
            slotProps={{ root: { slotProps: { backdrop: { style: { backgroundColor: 'transparent' } } } } }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: -65,
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {isDeactivatedTab ? (
              <MenuItem
                onClick={() => {
                  onReactivate();
                  closeActionsMenu();
                }}
              >
                Reactivate rider(s)
              </MenuItem>
            ) : (
              <>
                <MenuItem
                  onClick={() => {
                    onMoveToInvoiceGroup();
                    closeActionsMenu();
                  }}
                >
                  Move to invoice group
                </MenuItem>
                <Divider sx={{ borderColor: colorTokens.border.subtle }} />
                <MenuItem
                  onClick={() => {
                    onDeactivate();
                    closeActionsMenu();
                  }}
                  sx={{ color: colorTokens.error.main }}
                >
                  Deactivate rider(s)
                </MenuItem>
              </>
            )}
          </Menu>
        </>
      ) : (
        <>
          {!isDeactivatedTab && (
            <Button color="secondary" size="small" onClick={onMoveToInvoiceGroup}>
              Move to invoice group
            </Button>
          )}
          <Button color="tertiary" size="small" onClick={onClearSelection}>
            Clear selection
          </Button>
        </>
      )}
    </>
  );
};

export const RidersSelectionBanner = ({
  selectedRiderIds,
  pageRiderCount,
  totalCount,
  isAllFilteredSelected,
  onSelectAllFiltered,
  onClearSelection,
  onMoveSuccess,
  queryParams,
}: RidersSelectionBannerProps) => {
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const { enableBulkRiderStatusChange } = useFlags();
  const isDeactivatedTab = queryParams.state === 'deactivated';
  const { showSelectAllFiltered, selectionText } = getSelectionState(
    selectedRiderIds,
    pageRiderCount,
    totalCount,
    isAllFilteredSelected
  );

  return (
    <>
      <BannerContainer>
        <Text>{selectionText}</Text>
        <SelectionActions
          showSelectAllFiltered={showSelectAllFiltered}
          totalCount={totalCount}
          onSelectAllFiltered={onSelectAllFiltered}
          onMoveToInvoiceGroup={() => setIsMoveModalOpen(true)}
          onDeactivate={() => setIsDeactivateModalOpen(true)}
          onReactivate={() => setIsReactivateModalOpen(true)}
          isDeactivatedTab={isDeactivatedTab}
          showActionsMenu={!!enableBulkRiderStatusChange}
          onClearSelection={onClearSelection}
        />
      </BannerContainer>

      <MoveToInvoiceGroupModal
        open={isMoveModalOpen}
        queryParams={queryParams}
        selectedRiderIds={selectedRiderIds}
        isAllFilteredSelected={isAllFilteredSelected}
        totalCount={totalCount}
        onClose={() => setIsMoveModalOpen(false)}
        onSuccess={() => {
          onClearSelection();
          onMoveSuccess();
          setIsMoveModalOpen(false);
        }}
      />

      <BulkDeactivateModal
        open={isDeactivateModalOpen}
        queryParams={queryParams}
        selectedRiderIds={selectedRiderIds}
        isAllFilteredSelected={isAllFilteredSelected}
        totalCount={totalCount}
        onClose={() => setIsDeactivateModalOpen(false)}
        onSuccess={() => {
          onClearSelection();
          setIsDeactivateModalOpen(false);
        }}
      />

      <BulkReactivateModal
        open={isReactivateModalOpen}
        queryParams={queryParams}
        selectedRiderIds={selectedRiderIds}
        isAllFilteredSelected={isAllFilteredSelected}
        totalCount={totalCount}
        onClose={() => setIsReactivateModalOpen(false)}
        onSuccess={() => {
          onClearSelection();
          setIsReactivateModalOpen(false);
        }}
      />
    </>
  );
};

const BannerContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  background-color: ${colorPalette.blue98};
  border-radius: 5px;
  margin-bottom: 12px;

  ${variables.mediaQuery.lessThan('desktop')`
    flex-direction: column;
  `};
`;
